import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Cube, Spinner, Eye, EyeSlash } from "@phosphor-icons/react";
import GoogleSignInButton from "@/components/GoogleSignInButton";
import { useIndustries, IndustryBubbleBar, IndustryPanel } from "@/components/IndustryBubbles";

export default function Register() {
  const [form, setForm] = useState({ name: "", company_name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const industries = useIndustries();
  const { login } = useAuth();
  const navigate = useNavigate();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/register", { ...form, industry: industries.selected });
      localStorage.removeItem("orbit_industry");
      login(data.access_token, data.user);
      navigate("/app/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#FAFAFB] overflow-x-hidden">
      <div className="flex items-center justify-center p-6 order-2 lg:order-1 min-w-0">
        <div className="w-full max-w-sm min-w-0">
          <Link to="/" className="flex items-center gap-2 mb-8">
            <div className="h-8 w-8 rounded-md bg-emerald-500 flex items-center justify-center"><Cube size={18} weight="bold" className="text-white" /></div>
            <span className="font-display font-bold text-lg">Orbit Inventory Management</span>
          </Link>
          <h1 className="font-display text-3xl font-bold tracking-tight">Create your account</h1>
          <p className="text-zinc-500 mt-1 mb-6">Set up your company and start tracking assets.</p>
          <div className="mb-6">
            <Label className="text-sm font-semibold">What will you track?</Label>
            <p className="text-xs text-zinc-500 mb-2">We'll set up the right inventory types and locations for you.</p>
            <IndustryBubbleBar list={industries.list} selected={industries.selected} onSelect={industries.select} compact />
            {industries.current && <p className="text-xs text-emerald-700 mt-1" data-testid="register-industry-note">Includes {industries.current.asset_types.map((t) => t.label).join(", ")} and {industries.current.locations.length} ready-made locations.</p>}
          </div>

          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label className="text-sm font-semibold">Company name</Label>
              <Input value={form.company_name} onChange={set("company_name")} required className="h-12 mt-1.5 focus:ring-2 focus:ring-emerald-500" data-testid="register-company" placeholder="Shelton Auto Group" />
            </div>
            <div>
              <Label className="text-sm font-semibold">Your name</Label>
              <Input value={form.name} onChange={set("name")} required className="h-12 mt-1.5 focus:ring-2 focus:ring-emerald-500" data-testid="register-name" placeholder="John Doe" />
            </div>
            <div>
              <Label className="text-sm font-semibold">Email</Label>
              <Input type="email" value={form.email} onChange={set("email")} required className="h-12 mt-1.5 focus:ring-2 focus:ring-emerald-500" data-testid="register-email" placeholder="you@company.com" />
            </div>
            <div>
              <Label className="text-sm font-semibold">Password</Label>
              <div className="relative mt-1.5">
                <Input type={showPw ? "text" : "password"} value={form.password} onChange={set("password")} required minLength={6} className="h-12 pr-11 focus:ring-2 focus:ring-emerald-500" data-testid="register-password" placeholder="At least 6 characters" />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-700" aria-label={showPw ? "Hide password" : "Show password"} data-testid="toggle-password">
                  {showPw ? <EyeSlash size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            {error && <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2.5" data-testid="register-error">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full h-12 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold" data-testid="register-submit">
              {loading ? <Spinner size={20} className="animate-spin" /> : "Create Account"}
            </Button>
          </form>
          <GoogleSignInButton label="Sign up with Google" />
          <p className="mt-6 text-sm text-zinc-500 text-center">Already have an account? <Link to="/login" className="text-emerald-600 font-semibold" data-testid="to-login">Sign in</Link></p>
        </div>
      </div>
      <div className="hidden lg:block relative bg-zinc-950 order-1 lg:order-2 overflow-y-auto max-h-screen">
        <img src="https://images.pexels.com/photos/3856118/pexels-photo-3856118.jpeg" alt="Workshop" className="absolute inset-0 w-full h-full object-cover opacity-40" />
        <div className="relative z-10 p-12 min-h-full flex flex-col justify-end gap-6">
          {industries.current && <IndustryPanel industry={industries.current} showCta={false} />}
          <div>
            <h2 className="font-display text-4xl font-extrabold text-white leading-tight">Built for the lot,<br />the shop, and the road.</h2>
            <p className="text-zinc-400 mt-4 max-w-md">Role-based access, QR scanning, and a permanent movement ledger — all in one place.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
